import React, { useState } from 'react';
import { FAQS, FEMALE_FAQS } from '../data/faqs';
import { HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useSite } from '../context/SiteContext';
import { SpecialistAvatar } from './SpecialistAvatar';

interface FaqSectionProps {
  customFaqs?: Array<{ id: string; question: string; answer: string; category?: string }>;
}

export const FaqSection: React.FC<FaqSectionProps> = ({ customFaqs }) => {
  let isFemale = false;
  try {
    const site = useSite();
    if (site) isFemale = site.isFemale;
  } catch {}

  const currentFaqs = customFaqs || (isFemale ? FEMALE_FAQS : FAQS);
  const [openId, setOpenId] = useState<string>(isFemale ? 'f1' : '1');

  const toggleFaq = (id: string) => {
    setOpenId(openId === id ? '' : id);
  };

  return (
    <section id="sss" className="py-12 sm:py-16 bg-slate-50 border-t border-slate-200 relative">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-8 lg:px-12 xl:px-16">
        
        {/* Section Header */}
        <div className="text-center max-w-4xl mx-auto space-y-2.5 mb-8">
          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold shadow-2xs ${
            isFemale ? 'bg-rose-50 border border-rose-200 text-rose-900' : 'bg-amber-50 border border-amber-300 text-amber-900'
          }`}>
            <HelpCircle className={`w-3.5 h-3.5 ${isFemale ? 'text-rose-600' : 'text-amber-600'}`} />
            <span>{isFemale ? 'Kadın Sağlığı & Güvenlik Standartları' : 'Merak Edilenler & Güvenlik'}</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
            {isFemale ? 'Bursa Kadın Lazer' : 'Sıkça Sorulan'}{' '}
            <span className={isFemale ? 'text-rose-600' : 'text-amber-600'}>
              {isFemale ? 'Sıkça Sorulan Sorular' : 'Sorular'}
            </span>
          </h2>
          <p className="text-slate-600 text-xs sm:text-sm md:text-base leading-relaxed font-normal">
            {isFemale
              ? 'Bursa Nilüfer FSM Bulvarı kadın buz lazer epilasyon seansları, genital bölge güvenliği, batık ve çilek bacak tedavisi hakkında en çok merak edilenler.'
              : 'Bursa Nilüfer FSM Bulvarı erkek lazer epilasyon seansları, fiyatlandırma ilkeleri ve acısız buz başlık teknolojisi hakkında en çok sorulanlar.'}
          </p>
        </div>

        {/* 2-Column Responsive Independent Accordion Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8 items-start">
          {/* Left Column */}
          <div className="space-y-3 flex flex-col">
            {currentFaqs.filter((_, idx) => idx % 2 === 0).map((faq) => {
              const isOpen = openId === faq.id;
              return (
                <div
                  key={faq.id}
                  id={`faq-item-${faq.id}`}
                  className="bg-white border border-slate-200 rounded-2xl overflow-hidden transition-all shadow-2xs"
                >
                  <button
                    onClick={() => toggleFaq(faq.id)}
                    className={`w-full p-4 text-left flex items-start justify-between gap-3 text-slate-900 ${isFemale ? 'hover:text-rose-700' : 'hover:text-amber-700'} transition-colors cursor-pointer`}
                    aria-expanded={isOpen}
                  >
                    <span className="text-xs sm:text-sm font-bold flex items-start gap-2.5">
                      <span className={`w-5 h-5 rounded-full ${isFemale ? 'bg-rose-50 border border-rose-200 text-rose-800' : 'bg-amber-50 border border-amber-300 text-amber-800'} text-[11px] font-black flex items-center justify-center shrink-0 mt-0.5`}>
                        ?
                      </span>
                      <span className="leading-snug">{faq.question}</span>
                    </span>
                    <div className="text-slate-400 shrink-0 mt-0.5">
                      {isOpen ? (
                        <ChevronUp className={`w-4 h-4 ${isFemale ? 'text-rose-600' : 'text-amber-600'}`} />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 text-xs text-slate-600 leading-relaxed border-t border-slate-100 bg-slate-50/50">
                      <p className="pl-7">{faq.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Right Column */}
          <div className="space-y-3 flex flex-col">
            {currentFaqs.filter((_, idx) => idx % 2 !== 0).map((faq) => {
              const isOpen = openId === faq.id;
              return (
                <div
                  key={faq.id}
                  id={`faq-item-${faq.id}`}
                  className="bg-white border border-slate-200 rounded-2xl overflow-hidden transition-all shadow-2xs"
                >
                  <button
                    onClick={() => toggleFaq(faq.id)}
                    className={`w-full p-4 text-left flex items-start justify-between gap-3 text-slate-900 ${isFemale ? 'hover:text-rose-700' : 'hover:text-amber-700'} transition-colors cursor-pointer`}
                    aria-expanded={isOpen}
                  >
                    <span className="text-xs sm:text-sm font-bold flex items-start gap-2.5">
                      <span className={`w-5 h-5 rounded-full ${isFemale ? 'bg-rose-50 border border-rose-200 text-rose-800' : 'bg-amber-50 border border-amber-300 text-amber-800'} text-[11px] font-black flex items-center justify-center shrink-0 mt-0.5`}>
                        ?
                      </span>
                      <span className="leading-snug">{faq.question}</span>
                    </span>
                    <div className="text-slate-400 shrink-0 mt-0.5">
                      {isOpen ? (
                        <ChevronUp className={`w-4 h-4 ${isFemale ? 'text-rose-600' : 'text-amber-600'}`} />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 text-xs text-slate-600 leading-relaxed border-t border-slate-100 bg-slate-50/50">
                      <p className="pl-7">{faq.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Can't find question CTA with Specialist */}
        <div className="bg-white p-5 sm:p-6 rounded-2xl border border-slate-200 max-w-lg mx-auto shadow-sm flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
          <SpecialistAvatar size="md" showOnlineStatus={true} />
          <div className="space-y-1 flex-1">
            <h3 className="text-xs sm:text-sm font-bold text-slate-900">Aklınıza takılan farklı bir soru mu var?</h3>
            <p className="text-xs text-slate-600">
              Uzman estetisyenimiz canlı destek üzerinden seans ve kıl yapısı sorularınızı anında doğrudan yanıtlasın.
            </p>
            <div className="pt-1.5 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  if (typeof window !== 'undefined' && window.openLiveChat) {
                    window.openLiveChat();
                  } else if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('open-live-chat'));
                  }
                }}
                className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs transition-all shadow-2xs border ${
                  isFemale ? 'border-rose-500/40 hover:border-rose-400' : 'border-amber-500/40 hover:border-amber-400'
                } group cursor-pointer active:scale-95`}
              >
                <SpecialistAvatar size="xs" showOnlineStatus={true} bordered={false} />
                <span className={isFemale ? 'group-hover:text-rose-300 transition-colors' : 'group-hover:text-amber-300 transition-colors'}>
                  Canlı Destekten Uzmanımıza Danışın
                </span>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              </button>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};
